import { useCallback, useState } from 'react';
import type { RangeSensitivity } from 'api/models/RangeSensitivity';
import { valuationsAction } from 'common/actions';
import { ValuationByAllocation } from 'common/types/valuation/valuation';
import { getStringValue } from 'utilities';
import { VALIDATED } from '../../../../common/constants/general';
import { useStore } from '../../../../common/store';
import { UseStoreValues } from '../../../../common/types/store';
import { Tab } from '../../../../components';
import { VALUATION_SUMMARY_KEY } from '../../common/constants/valuationSummary';
import { useApproachQueryParam } from '../useApproachQueryParam';
import { UseCreateRangeSensitivityParams } from '../useCreateValuation/types';

const useCreateRangeSensitivity = (params: UseCreateRangeSensitivityParams) => {
  const {
    valuation,
    REDIRECT_TIMEOUT_DELAY,
    setValuation,
    setPageStatus,
    setArePendingChanges,
    setIsEditingApproachName,
    setIsEditingCalibrationName,
    setIsEditingRangeSensitivityName,
    setIsChangingFinancialStatement,
    handleMenuClose,
    setApproachToUpdate,
    handleOpenUpdateApproachDialog,
    setCalibrationToUpdate,
  } = params;
  const [, dispatch] = useStore() as unknown as UseStoreValues;
  const [rangeSensitivityToUpdate, setRangeSensitivityToUpdate] = useState<RangeSensitivity | null>(null);
  const [rangeSensitivityTabs, setRangeSensitivityTabs] = useState<Tab[]>([]);

  const { setApproachQueryParam } = useApproachQueryParam();

  const handleDeleteRangeSensitivity = useCallback(
    (panelIdParam: string) => {
      if (panelIdParam) {
        valuation.range_sensitivities = valuation.range_sensitivities?.filter(rs => {
          const id = getStringValue((rs as RangeSensitivity & { panelId?: string }).panelId ?? rs.panel_id);
          return id !== panelIdParam;
        });

        dispatch?.(valuationsAction.setValuationInfo(valuation));
        setValuation(valuation);

        setPageStatus(VALIDATED);
        setArePendingChanges(true);

        setTimeout(() => {
          setApproachQueryParam(VALUATION_SUMMARY_KEY);
        }, REDIRECT_TIMEOUT_DELAY);
      }
    },
    [
      valuation,
      dispatch,
      setValuation,
      setPageStatus,
      setArePendingChanges,
      REDIRECT_TIMEOUT_DELAY,
      setApproachQueryParam,
    ]
  );

  const handleUpdateRangeSensitivityDialog = useCallback(
    (panelIdParam: string) => {
      if (panelIdParam) {
        handleMenuClose();

        const toUpdate = valuation.range_sensitivities?.find(rs => {
          const id = getStringValue((rs as RangeSensitivity & { panelId?: string }).panelId ?? rs.panel_id);
          return id === panelIdParam;
        });

        if (toUpdate) {
          setApproachToUpdate(null);
          setCalibrationToUpdate(null);
          handleOpenUpdateApproachDialog();
          setRangeSensitivityToUpdate(toUpdate);
        }
      }
    },
    [
      handleMenuClose,
      handleOpenUpdateApproachDialog,
      setApproachToUpdate,
      setCalibrationToUpdate,
      valuation.range_sensitivities,
    ]
  );

  const showUpdateRangeSensitivityNameDialog = useCallback(
    (panelIdParam: string) => {
      if (panelIdParam) {
        setIsEditingApproachName(false);
        setIsEditingCalibrationName(false);
        setIsEditingRangeSensitivityName(true);
        setIsChangingFinancialStatement(false);
        handleUpdateRangeSensitivityDialog(panelIdParam);
      }
    },
    [
      handleUpdateRangeSensitivityDialog,
      setIsEditingApproachName,
      setIsEditingCalibrationName,
      setIsEditingRangeSensitivityName,
      setIsChangingFinancialStatement,
    ]
  );

  const updateValuationRangeSensitivities = useCallback(
    (updated: RangeSensitivity[]) => {
      const updatedValuation = { ...valuation, range_sensitivities: updated } as ValuationByAllocation;

      dispatch?.(valuationsAction.setValuationInfo(updatedValuation));
      setValuation(updatedValuation);
    },
    [dispatch, setValuation, valuation]
  );

  return {
    handleDeleteRangeSensitivity,
    showUpdateRangeSensitivityNameDialog,
    updateValuationRangeSensitivities,
    setRangeSensitivityToUpdate,
    rangeSensitivityToUpdate,
    setRangeSensitivityTabs,
    rangeSensitivityTabs,
  };
};

export default useCreateRangeSensitivity;
